import React, { useRef, useState } from 'react';
import { Star, X, GripVertical, ImagePlus, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * MultiImageUploader — controlled multi-image field.
 *
 * value:    array of items { id?|tempId, url, file?, isCover, order, pending? }
 * onChange: (nextItems) => void
 *
 * Features: drag & drop + multi-select, live previews, delete, reorder (drag or
 * arrow buttons), and choosing the cover. The first image is the cover by
 * default. Purely presentational — uploading happens on form save.
 */
const MAX_MB = 5;
let _seq = 0;
const nextTempId = () => `tmp_${Date.now()}_${_seq++}`;

export default function MultiImageUploader({ value = [], onChange, isAr = false }) {
  const inputRef = useRef(null);
  const [dragOverZone, setDragOverZone] = useState(false);
  const dragIndex = useRef(null);
  const [overIndex, setOverIndex] = useState(null);

  const t = (fr, ar) => (isAr ? ar : fr);

  const emit = (items) => {
    // Normalise order + guarantee a single cover.
    const withOrder = items.map((it, i) => ({ ...it, order: i }));
    if (withOrder.length && !withOrder.some((i) => i.isCover)) withOrder[0].isCover = true;
    let coverSeen = false;
    withOrder.forEach((i) => {
      if (i.isCover && !coverSeen) coverSeen = true;
      else if (i.isCover) i.isCover = false;
    });
    onChange(withOrder);
  };

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f && f.type.startsWith('image/'));
    const accepted = [];
    for (const file of files) {
      if (file.size > MAX_MB * 1024 * 1024) {
        alert(t(`Image « ${file.name} » trop grande (max ${MAX_MB} Mo)`, `الصورة « ${file.name} » كبيرة جداً (الأقصى ${MAX_MB} ميجا)`));
        continue;
      }
      accepted.push({
        tempId: nextTempId(),
        url: URL.createObjectURL(file),
        file,
        isCover: false,
        pending: true,
      });
    }
    if (accepted.length) emit([...value, ...accepted]);
  };

  const onInputChange = (e) => { addFiles(e.target.files); e.target.value = ''; };

  const removeAt = (idx) => {
    const it = value[idx];
    if (it?.pending && it.url) { try { URL.revokeObjectURL(it.url); } catch (_) {} }
    emit(value.filter((_, i) => i !== idx));
  };

  const setCover = (idx) => emit(value.map((it, i) => ({ ...it, isCover: i === idx })));

  const move = (from, to) => {
    if (to < 0 || to >= value.length || from === to) return;
    const arr = [...value];
    const [m] = arr.splice(from, 1);
    arr.splice(to, 0, m);
    emit(arr);
  };

  const key = (it, i) => it.id || it.tempId || i;

  return (
    <div>
      {/* Drop zone */}
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOverZone(true); }}
        onDragLeave={() => setDragOverZone(false)}
        onDrop={(e) => { e.preventDefault(); setDragOverZone(false); addFiles(e.dataTransfer.files); }}
        className={`flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-2xl border-2 border-dashed px-4 py-6 text-center transition-colors ${
          dragOverZone ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50/40'
        }`}
      >
        <ImagePlus className="h-7 w-7 text-blue-500" />
        <div className="text-sm font-semibold text-gray-700">
          {t('Glissez vos photos ici ou cliquez', 'اسحب الصور هنا أو انقر')}
        </div>
        <div className="text-[11px] text-gray-400">
          {t(`Plusieurs images • JPG, PNG, WEBP • max ${MAX_MB} Mo`, `عدة صور • JPG, PNG, WEBP • الأقصى ${MAX_MB} ميجا`)}
        </div>
        <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={onInputChange} />
      </div>

      {/* Previews */}
      {value.length > 0 && (
        <>
          <div className="mt-3 grid grid-cols-3 gap-2.5 sm:grid-cols-4">
            {value.map((it, i) => (
              <div
                key={key(it, i)}
                draggable
                onDragStart={() => { dragIndex.current = i; }}
                onDragOver={(e) => { e.preventDefault(); setOverIndex(i); }}
                onDrop={(e) => { e.preventDefault(); move(dragIndex.current, i); dragIndex.current = null; setOverIndex(null); }}
                onDragEnd={() => { dragIndex.current = null; setOverIndex(null); }}
                className={`group relative aspect-[4/3] overflow-hidden rounded-xl border bg-gray-50 ${
                  it.isCover ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
                } ${overIndex === i ? 'ring-2 ring-blue-300' : ''}`}
              >
                <img src={it.url} alt="" className="h-full w-full object-cover" loading="lazy" />

                {/* Cover badge */}
                {it.isCover && (
                  <span className="absolute left-1.5 top-1.5 z-10 inline-flex items-center gap-1 rounded-full bg-blue-600 px-1.5 py-0.5 text-[9px] font-bold text-white shadow">
                    <Star className="h-2.5 w-2.5 fill-current" /> {t('Couverture', 'الغلاف')}
                  </span>
                )}

                {/* Drag handle */}
                <span className="absolute right-1.5 top-1.5 z-10 hidden rounded bg-black/40 p-0.5 text-white group-hover:block">
                  <GripVertical className="h-3.5 w-3.5" />
                </span>

                {/* Hover controls */}
                <div className="absolute inset-x-0 bottom-0 z-10 flex items-center justify-between gap-1 bg-gradient-to-t from-black/70 to-transparent px-1.5 pb-1.5 pt-4 opacity-0 transition-opacity group-hover:opacity-100">
                  <div className="flex gap-1">
                    <button type="button" onClick={(e) => { e.stopPropagation(); move(i, i - 1); }} title={t('Déplacer', 'تحريك')}
                      className="rounded bg-white/90 p-1 text-gray-700 hover:bg-white disabled:opacity-40" disabled={i === 0}>
                      <ChevronLeft className="h-3.5 w-3.5" />
                    </button>
                    <button type="button" onClick={(e) => { e.stopPropagation(); move(i, i + 1); }} title={t('Déplacer', 'تحريك')}
                      className="rounded bg-white/90 p-1 text-gray-700 hover:bg-white disabled:opacity-40" disabled={i === value.length - 1}>
                      <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="flex gap-1">
                    {!it.isCover && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); setCover(i); }} title={t('Définir comme couverture', 'تعيين كغلاف')}
                        className="rounded bg-white/90 p-1 text-amber-500 hover:bg-white">
                        <Star className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button type="button" onClick={(e) => { e.stopPropagation(); removeAt(i); }} title={t('Supprimer', 'حذف')}
                      className="rounded bg-white/90 p-1 text-red-500 hover:bg-white">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-gray-400">
            {t('La 1ʳᵉ image (★) est la couverture. Glissez pour réordonner.', 'الصورة الأولى (★) هي الغلاف. اسحب لإعادة الترتيب.')}
          </p>
        </>
      )}
    </div>
  );
}
